import React, {useCallback, useContext, useEffect, useState, useMemo, Fragment} from 'react'
import {KTSVG} from '../../../../_metronic/helpers'
import {shallowEqual, useDispatch, useSelector} from 'react-redux'
import {Link} from 'react-router-dom'
import * as PurchaseOrderReturnAction from '../redux/PurchaseOrderReturnAction'
import {RootState} from '../../../../setup/redux/RootReducer'
import {formatDateTime, formatMoney} from '../../../helper'
import {Route, Breadcrumbs} from '../../../components/Breadcrumbs'
import {ApprovalLegend} from '../../../components/ApprovalLegend'
import {AbilityContext, Can} from '../../../rbac/Can'
import {
  PurchaseOrderReturnInitValues,
  PurchaseOrderReturnModel,
  PurchaseOrderReturnSearchModelInitValues,
  PurchaseOrderReturnTableModelInitValues,
} from '../models/PurchaseOrderReturnModel'
import {Table} from '../../../components/ReactTable'
import {Field, Form, Formik} from 'formik'
import {DateRangePicker} from 'rsuite'
import * as Yup from 'yup'
import {PurchaseOrderReturnDeleteConfirmModal} from './PurchaseOrderReturnDeleteConfirmModal'

type Props = {
  className: string
}

export const defaultColumnSizing = {
  size: 150,
  minSize: 20,
  maxSize: Number.MAX_SAFE_INTEGER,
}

export const PurchaseOrdersReturn: React.FC<Props> = ({className}) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [toDelete, setToDelete] = useState<PurchaseOrderReturnModel>(PurchaseOrderReturnInitValues)
  const dispatch = useDispatch()

  const loading: any = useSelector<RootState>(
    ({purchaseOrderReturns}) => purchaseOrderReturns.loading,
    shallowEqual
  )
  const purchaseOrderReturns: any = useSelector<RootState>(
    ({purchaseOrderReturns}) => purchaseOrderReturns.purchaseOrderReturns,
    shallowEqual
  )
  const pages: any = useSelector<RootState>(
    ({purchaseOrderReturns}) => purchaseOrderReturns.pagination?.pages,
    shallowEqual
  )

  const [searchParams, setSearchParams]: any = useState({})
  const [createdAt, setCreatedAt]: any = useState([])
  const [approvedAt, setApprovedAt]: any = useState([])
  const [show, setShow] = useState(false)
  const filterHandel = () => {
    setShow(!show)
  }

  useEffect(() => {
    dispatch(PurchaseOrderReturnAction.fetchAll(PurchaseOrderReturnTableModelInitValues))
  }, [dispatch])

  const fetchData = useCallback(
    ({pageSize, pageIndex, params, sortBy}: any) => {
      const queryParams = {
        page: pageIndex + 1,
        sortBy: sortBy[0]?.id || 'createdAt',
        sortOrder: sortBy[0]?.desc === false ? 'asc' : 'desc',
        search: params,
      }
      dispatch(PurchaseOrderReturnAction.fetchAll(queryParams))
    },
    [searchParams]
  )
  const ability = useContext(AbilityContext)

  const columns = useMemo(
    () => [
      {
        Header: 'Created Date',
        accessor: 'createdAt',
        Cell: (params: any) => {
          if (params?.row?.original?.createdAt) {
            return formatDateTime(params?.row?.original?.createdAt)
          } else {
            return '-'
          }
        },
        show: true,
      },
      {
        Header: 'Purchase Order Return ',
        accessor: 'number',
        Cell: (params: any) => (
          <Link to={`/purchase-order-returns/${params?.row?.original?._id}`}>
            {params?.row?.original?.number}
          </Link>
        ),
        show: true,
      },
      {
        Header: 'Goods Received Note  ',
        accessor: 'goodsReceivedNote.number',
        Cell: (params: any) => (
          <Fragment>
            {ability.can('read', 'purchase-order-returns') ? (
              <Link to={`/goods-received-note/${params?.row?.original?.goodsReceivedNote?._id}`}>
                {params?.row?.original?.goodsReceivedNote?.number}
              </Link>
            ) : (
              params?.row?.original?.goodsReceivedNote?.number
            )}
          </Fragment>
        ),
        disableSortBy: true,
      },
      {
        Header: 'Purchase Order ',
        accessor: 'purchaseOrder.number',
        Cell: (params: any) => (
          <Fragment>
            {ability.can('read', 'purchase-order-returns') ? (
              <Link to={`/purchase-orders/${params?.row?.original?.purchaseOrder?._id}`}>
                {params?.row?.original?.purchaseOrder?.number}
              </Link>
            ) : (
              params?.row?.original?.purchaseOrder?.number
            )}
          </Fragment>
        ),
        disableSortBy: true,
      },
      {Header: 'Purchase For', accessor: 'purchaseFor', disableSortBy: true},
      {Header: 'Supplier Name', accessor: 'supplier.name', disableSortBy: true},
      {
        Header: 'Total Items',
        accessor: 'items.length',
        disableSortBy: true,
      },
      {
        Header: 'Total Amount',
        accessor: 'totalAmount',
        Cell: (params: any) => {
          return formatMoney(params?.row?.original?.totalAmount)
        },
      },
      {Header: 'Status', accessor: 'status', show: true},
      {
        Header: 'Approved By',
        accessor: 'approvedBy.fullName',
        Cell: (params: any) => {
          if (!params?.row?.original?.approvedBy) return '-'
          return params?.row?.original?.approvedBy?.fullName
        },
        disableSortBy: true,
      },
      {
        Header: 'Approved Date',
        accessor: 'approvedAt',
        Cell: (params: any) => {
          if (!params?.row?.original?.approvedBy) return '-'
          return formatDateTime(params?.row?.original?.approvedAt)
        },
        show: true,
      },
      {
        Header: 'Actions',
        Cell: (params: any) => (
          <div className='d-flex flex-shrink-0'>
            <Can do='approve' on='purchase-order-returns'>
              <div>
                {params?.row?.original?.status === 'NEW' ? (
                  <Link to={`/purchase-order-returns/${params?.row?.original?._id}/approve`}>
                    <button
                      className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                      data-bs-toggle='tooltip'
                      title='Approval'
                    >
                      <KTSVG
                        path='/media/icons/duotune/general/gen026.svg'
                        className='svg-icon-primary svg-icon-3'
                      />
                    </button>
                  </Link>
                ) : params?.row?.original?.status === 'APPROVED' ||
                  params?.row?.original?.status === 'COMPLETED' ? (
                  <button
                    className='btn btn-icon btn-bg-light btn-active-color-success btn-sm me-1'
                    data-bs-toggle='tooltip'
                    title='Approved'
                  >
                    <KTSVG
                      path='/media/icons/duotune/general/gen026.svg'
                      className='svg-icon-success svg-icon-3'
                    />
                  </button>
                ) : (
                  <button
                    className='btn btn-icon btn-bg-light btn-active-color-default btn-sm me-1'
                    data-bs-toggle='tooltip'
                    title='Rejected'
                  >
                    <KTSVG
                      path='/media/icons/duotune/general/gen026.svg'
                      className='svg-icon-default svg-icon-3'
                    />
                  </button>
                )}
              </div>
            </Can>

            <Can do='delete' on='purchase-order-returns'>
              {params?.row?.original?.status === 'NEW' && (
                <button
                  className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                  data-bs-toggle='modal'
                  data-bs-target='#kt_modal_delete_app'
                  title='Delete Purchase Order Return'
                  onClick={() => {
                    setToDelete(params?.row?.original)
                    setShowDeleteDialog(true)
                  }}
                >
                  <KTSVG
                    path='/media/icons/duotune/general/gen027.svg'
                    className='m-3 svg-icon-3'
                  />
                </button>
              )}
            </Can>
          </div>
        ),
      },
    ],
    []
  )

  const routes: Route[] = [
    {name: 'Dashboard', url: '/dashboard'},
    {name: 'Purchase Order Returns List', url: '/purchase-order-returns'},
  ]

  return (
    <>
      <PurchaseOrderReturnDeleteConfirmModal
        show={showDeleteDialog}
        handleClose={() => {
          setShowDeleteDialog(false)
        }}
        purchaseOrderReturn={toDelete}
      />

      <div className={`card card-custom ${className}`}>
        <Breadcrumbs routes={routes} />
        <div className='card-header border-0 pt-5'>
          <h1 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-3 mb-1'>Purchase Order Returns List</span>
          </h1>
          <ApprovalLegend />
          <Can do='create' on='purchase-order-returns'>
            <div
              className='card-toolbar'
              data-bs-toggle='tooltip'
              data-bs-placement='top'
              data-bs-trigger='hover'
              title='Add Purchase Order Return'
            >
              <Link
                to='/purchase-order-returns/add'
                className='btn btn-sm btn-light-primary'
                data-bs-toggle='modal'
                data-bs-target='#kt_modal_create_app'
                id='kt_toolbar_primary_button'
              >
                <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-3' />
                Add Purchase Order Return
              </Link>
            </div>
          </Can>
        </div>

        <div className='card-body'>
          <div
            className={`d-flex align-items-center ${
              !show ? 'justify-content-end' : 'justify-content-between'
            }`}
          >
            <p className='my-0 me-3' style={{color: '#004B87', fontSize: '14px'}}>
              <b>Search Filters</b>
            </p>
            <span onClick={filterHandel}>
              <img src='/media/svg/icons/General/filter.svg' alt='' />
            </span>
          </div>
          {show ? (
            <Formik
              initialValues={PurchaseOrderReturnSearchModelInitValues}
              validationSchema={Yup.object({
                number: Yup.string(),
                status: Yup.string(),
                createdAt: Yup.array(Yup.string()),
                approvedAt: Yup.array(Yup.string()),
                approvedBy: Yup.string(),
              })}
              onSubmit={(values: any) => {
                if (values.status === 'Select Status') {
                  values.status = ''
                }

                setSearchParams(values)
              }}
            >
              {({values, setFieldValue}) => (
                <Form>
                  <div className='row row-cols-lg-2 row-cols-md-2 row-cols-1 g-5'>
                    <div className='form-group col'>
                      <label htmlFor='createdAt' className='fw-bold'>
                        Created Date
                      </label>
                      <DateRangePicker
                        value={createdAt}
                        block
                        name='createdAt'
                        cleanable={true}
                        size='md'
                        appearance='default'
                        character='-'
                        showOneCalendar
                        placeholder='Select Date'
                        format='dd/MM/yyyy'
                        onClean={(value: any) => {
                          setFieldValue('createdAt', '')
                        }}
                        onChange={(value: any) => {
                          setFieldValue('createdAt', value)
                          setCreatedAt(value)
                        }}
                      />
                    </div>
                    <div className='form-group col'>
                      <label htmlFor='number' className='fw-bold'>
                        Purchase Order Return
                      </label>
                      <Field
                        name='number'
                        placeholder='Purchase Order Return Number'
                        className='form-control form-control-lg'
                      />
                    </div>
                  </div>

                  <div className='row row-cols-lg-2 row-cols-md-2 row-cols-1 g-5 mt-1'>
                    <div className='form-group col'>
                      <label htmlFor='status' className='fw-bold'>
                        Status
                      </label>
                      <Field
                        as='select'
                        className='form-select form-select-md'
                        data-control='select2'
                        name='status'
                      >
                        <option>Status</option>
                        <option value='NEW'>NEW</option>
                        <option value='APPROVED'>APPROVED</option>
                        <option value='COMPLETED'>COMPLETED</option>
                        <option value='CANCELLED'>CANCELLED</option>
                        <option value='REJECTED'>REJECTED</option>
                      </Field>
                    </div>
                    <div className='form-group col'>
                      <label htmlFor='approvedAt' className='fw-bold'>
                        Approved Date
                      </label>
                      <DateRangePicker
                        value={approvedAt}
                        block
                        name='approvedAt'
                        cleanable={true}
                        size='md'
                        appearance='default'
                        character='-'
                        showOneCalendar
                        placeholder='Select Date'
                        format='dd/MM/yyyy'
                        onClean={(value: any) => {
                          setFieldValue('approvedAt', '')
                        }}
                        onChange={(value: any) => {
                          setFieldValue('approvedAt', value)
                          setApprovedAt(value)
                        }}
                      />
                    </div>
                  </div>
                  <div className='row row-cols-lg-2 row-cols-md-2 row-cols-1 g-5 mt-1'>
                    <div className='form-group col'>
                      <label htmlFor='number' className='fw-bold'>
                        Approved By
                      </label>
                      <Field
                        name='approvedBy'
                        placeholder='Approved By'
                        className='form-control form-control-lg'
                      />
                    </div>
                  </div>
                  <div className='d-flex justify-content-end mt-10'>
                    <Link
                      to='/purchase-order-returns'
                      className='btn btn-sm btn-light-primary me-3'
                      data-bs-toggle='tooltip'
                      data-bs-placement='top'
                      title='Cancel'
                      onClick={() => {
                        setSearchParams(PurchaseOrderReturnSearchModelInitValues)
                        setFieldValue('number', '')
                        setFieldValue('status', '')
                      }}
                    >
                      <span className='indicator-label'>Cancel</span>
                    </Link>

                    <button
                      type='submit'
                      className='btn btn-sm btn-primary'
                      data-bs-toggle='tooltip'
                      data-bs-placement='top'
                      title='Search'
                      id='liveToastBtn'
                    >
                      <span className='indicator-label'>Search</span>
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          ) : null}
          <div className='d-flex mt-5 '>
            <div className='ag-theme-material' style={{width: '100%'}}>
              <Table
                columns={columns}
                data={purchaseOrderReturns}
                params={searchParams}
                pageCount={pages}
                fetchData={fetchData}
                loading={loading}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
